<template>
  <div class="content-main-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Main Content</h1>
        <p>See where each item appears on the website, what it belongs to, and its content type.</p>
      </div>

      <button class="primary-btn" @click="openCreate">
        <i class="bx bx-plus"></i>
        Add Content
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">

      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-layout"></i>
        </div>
        <div>
          <span>Total Content</span>
          <strong>{{ totalContents }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-category"></i>
        </div>
        <div>
          <span>Categories</span>
          <strong>{{ totalCategories }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-grid-alt"></i>
        </div>
        <div>
          <span>Content Types</span>
          <strong>{{ totalTypes }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-calendar"></i>
        </div>
        <div>
          <span>This Month</span>
          <strong>{{ thisMonth }}</strong>
        </div>
      </div>

    </div>

    <!-- Main Card -->
    <div class="content-card">

      <!-- Toolbar -->
      <div class="toolbar">

        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search content..."
            @keyup.enter="applyFilters"
          />
        </div>

        <select
          v-model="typeFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Content Types</option>

          <option
            v-for="type in contentTypes"
            :key="type"
            :value="type"
          >
            {{ type }}
          </option>
        </select>

        <select
          v-model="parentTypeFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Website Areas</option>

          <option
            v-for="parentType in parentTypes"
            :key="parentType"
            :value="parentType"
          >
            {{ formatAreaLabel(parentType) }}
          </option>
        </select>

        <select
          v-model="categoryFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Categories</option>

          <option
            v-for="category in categories"
            :key="category.id"
            :value="category.id"
          >
            {{ category.name }}
          </option>
        </select>

        <button
          class="reset-btn"
          @click="resetFilters"
        >
          <i class="bx bx-reset"></i>
          Reset
        </button>

      </div>

      <!-- Table -->
      <div class="table-wrapper">

        <table>

          <thead>
            <tr>
              <th>Content</th>
              <th>Website Area</th>
              <th>Parent Record</th>
              <th>Category</th>
              <th>Content Type</th>
              <th>Created</th>
              <th class="action-column">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr
              v-for="item in contents"
              :key="item.con_id"
            >

              <!-- Content -->
              <td>
                <div class="content-info">

                  <div class="content-icon">
                    <i class="bx bx-file"></i>
                  </div>

                  <div>
                    <strong>
                      {{ item.con_title }}
                    </strong>

                    <small>
                      ID: #{{ item.con_id }}
                    </small>

                    <small
                      v-if="item.con_sub_title"
                      class="content-subtitle"
                    >
                      {{ item.con_sub_title }}
                    </small>
                  </div>

                </div>
              </td>

              <!-- Website Area -->
              <td>
                <span
                  v-if="item.con_parent_type"
                  class="area-badge"
                >
                  {{ formatAreaLabel(item.con_parent_type) }}
                </span>
                <span v-else class="muted">Unassigned</span>
              </td>

              <!-- Parent Record -->
              <td>
                <div class="parent-info">
                  <span v-if="item.con_parent">
                    {{ item.parent_title || `Record #${item.con_parent}` }}
                  </span>
                  <span v-else class="muted">
                    Top-level content
                  </span>
                  <small v-if="item.con_parent">
                    {{ formatLabel(item.con_parent_type) }} #{{ item.con_parent }}
                  </small>
                </div>
              </td>

              <!-- Category -->
              <td>
                <span
                  v-if="item.category_name"
                  class="category-badge"
                >
                  {{ item.category_name }}
                </span>

                <span
                  v-else
                  class="muted"
                >
                  —
                </span>
              </td>

              <!-- Content Type -->
              <td>
                <span class="type-badge">
                  {{ formatLabel(item.con_type) || '—' }}
                </span>
              </td>

              <!-- Created -->
              <td>
                <span class="date-text">
                  {{ formatDate(item.con_datetime) }}
                </span>
              </td>

              <!-- Actions -->
              <td>
                <div class="actions">

                  <button
                    class="icon-btn view"
                    title="View"
                    @click="openView(item)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="openEdit(item)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="deleteContent(item)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>
              </td>

            </tr>

            <tr v-if="contents.length === 0">
              <td
                colspan="7"
                class="empty-state"
              >
                <i class="bx bx-file-blank"></i>
                <h3>No content found</h3>
                <p>Try changing your search or filters.</p>
              </td>
            </tr>

          </tbody>

        </table>

      </div>

      <!-- Pagination -->
      <div
        class="pagination"
        v-if="totalPages > 1"
      >

        <div class="pagination-info">
          Page
          <strong>{{ page }}</strong>
          of
          <strong>{{ totalPages }}</strong>
        </div>

        <div class="pagination-buttons">

          <button
            :disabled="page === 1"
            @click="changePage(page - 1)"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="pageNumber in totalPages"
            :key="pageNumber"
            :class="{ active: page === pageNumber }"
            @click="changePage(pageNumber)"
          >
            {{ pageNumber }}
          </button>

          <button
            :disabled="page === totalPages"
            @click="changePage(page + 1)"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- Add / Edit / View Modal -->
    <div
      v-if="showModal"
      class="modal-overlay"
      @click.self="closeModal"
    >

      <div class="modal">

        <!-- Header -->
        <div class="modal-header">

          <div>
            <h2>
              {{ modalTitle }}
            </h2>

            <p>
              {{
                isViewMode
                  ? 'View main content information.'
                  : modalMode === 'edit'
                    ? 'Update main content details.'
                    : 'Create a new main content section.'
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <!-- Body -->
        <div class="modal-body">

          <div class="form-grid">

            <!-- Content ID -->
            <div
              v-if="isViewMode || modalMode === 'edit'"
              class="form-group"
            >
              <label>Content ID</label>

              <input
                :value="form.con_id"
                type="text"
                disabled
              />
            </div>

            <!-- Title -->
            <div class="form-group full">

              <label>
                Content Title
                <span v-if="!isViewMode">*</span>
              </label>

              <input
                v-model="form.con_title"
                type="text"
                placeholder="Enter content title"
                :disabled="isViewMode"
              />

            </div>

            <!-- Parent Type -->
            <div class="form-group">

              <label>Website Area</label>

              <select
                v-model="form.con_parent_type"
                :disabled="isViewMode"
                @change="changeParentType"
              >

                <option value="">
                  Select Website Area
                </option>

                <option
                  v-for="area in websiteAreaOptions"
                  :key="area"
                  :value="area"
                >
                  {{ formatAreaLabel(area) }}
                </option>

              </select>

            </div>

            <!-- Parent -->
            <div class="form-group">

              <label>Parent Record (optional)</label>

              <!-- Top-level website areas -->
              <select
                v-if="['home', 'event'].includes(form.con_parent_type)"
                v-model="form.con_parent"
                :disabled="isViewMode"
              >

                <option :value="null">
                  No linked record — {{ formatAreaLabel(form.con_parent_type) }}
                </option>

              </select>

              <!-- Course -->
              <select
                v-else-if="form.con_parent_type === 'course'"
                v-model.number="form.con_parent"
                :disabled="isViewMode"
              >

                <option :value="null">
                  Select Course
                </option>

                <option
                  v-for="course in courses"
                  :key="course.course_id"
                  :value="course.course_id"
                >
                  {{ course.title }}
                </option>

              </select>

              <!-- Creator -->
              <select
                v-else-if="form.con_parent_type === 'creator'"
                v-model.number="form.con_parent"
                :disabled="isViewMode"
              >

                <option :value="null">
                  Select Creator
                </option>

                <option
                  v-for="creator in creators"
                  :key="creator.creator_id"
                  :value="creator.creator_id"
                >
                  {{ creator.cre_username }}
                </option>

              </select>

              <!-- Blog -->
              <select
                v-else-if="form.con_parent_type === 'blog'"
                v-model.number="form.con_parent"
                :disabled="isViewMode"
              >

                <option :value="null">
                  Select Blog
                </option>

                <option
                  v-for="blog in blogs"
                  :key="blog.blog_id"
                  :value="blog.blog_id"
                >
                  {{ blog.blog_title }}
                </option>

              </select>

              <!-- Other website areas -->
              <select
                v-else
                disabled
              >
                <option>
                  Parent record selection is not available for this area
                </option>
              </select>

              <small class="field-help">
                Link course, creator, or blog content to its record. Home and event content can stay top-level.
              </small>

            </div>

            <!-- Category -->
            <div class="form-group">

              <label>Category</label>

              <select
                v-model.number="form.con_cat"
                :disabled="isViewMode"
              >

                <option :value="null">
                  Select Category
                </option>

                <option
                  v-for="category in categories"
                  :key="category.cat_id"
                  :value="category.cat_id"
                >
                  {{ category.cat_name }}
                </option>

              </select>

            </div>

            <!-- Content Type -->
            <div class="form-group">

              <label>Content Type</label>

              <select
                v-model="form.con_type"
                :disabled="isViewMode"
              >

                <option value="">
                  Select type
                </option>

                <option value="page">
                  Page
                </option>

                <option value="section">
                  Section
                </option>

                <option value="banner">
                  Banner
                </option>

                <option value="feature">
                  Feature
                </option>

                <option value="course">
                  Course
                </option>

                <option value="creator">
                  Creator
                </option>

                <option value="general">
                  General
                </option>

              </select>

            </div>

            <!-- Subtitle -->
            <div class="form-group full">

              <label>Sub Title</label>

              <textarea
                v-model="form.con_sub_title"
                rows="3"
                placeholder="Enter content subtitle"
                :disabled="isViewMode"
              ></textarea>

            </div>

            <!-- Meta Title -->
            <div class="form-group full">

              <label>Meta Title</label>

              <input
                v-model="form.con_meta_title"
                type="text"
                placeholder="Enter meta title"
                :disabled="isViewMode"
              />

            </div>

            <!-- Keywords -->
            <div class="form-group full">

              <label>Keywords</label>

              <textarea
                v-model="form.con_keyword"
                rows="3"
                placeholder="Enter keywords"
                :disabled="isViewMode"
              ></textarea>

            </div>

            <!-- Description -->
            <div class="form-group full">

              <label>Description</label>

              <textarea
                v-model="form.con_desc"
                rows="6"
                placeholder="Enter content description"
                :disabled="isViewMode"
              ></textarea>

            </div>

          </div>

        </div>

        <!-- Footer -->
        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeModal"
          >
            Close
          </button>

          <button
            v-if="isViewMode"
            class="primary-btn"
            @click="editFromView"
          >
            <i class="bx bx-edit"></i>
            Edit Content
          </button>

          <button
            v-else
            class="primary-btn"
            @click="saveContent"
          >
            <i class="bx bx-save"></i>

            {{
              modalMode === 'edit'
                ? 'Update Content'
                : 'Save Content'
            }}

          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getContentMainsApi,
  getContentMainStatsApi,
  getContentMainApi,
  createContentMainApi,
  updateContentMainApi,
  deleteContentMainApi
} from "@/apis/contentmainapis";

import { getCategoriesApi } from "@/apis/categoryapis";
import { getCoursesApi } from "@/apis/courseapis";
import { getCreatorsApi } from "@/apis/creatorapis";
import { getBlogsApi } from "@/apis/blogapis";

export default {
  name: "ContentMain",

  data() {
    return {
      loading: false,
      saving: false,

      contents: [],
      categories: [],

      courses: [],
      creators: [],
      blogs: [],

      totalContents: 0,
      totalCategories: 0,
      totalTypes: 0,
      thisMonth: 0,

      page: 1,
      limit: 10,
      totalPages: 1,

      search: "",
      typeFilter: "",
      parentTypeFilter: "",
      categoryFilter: "",

      showModal: false,
      modalMode: "view",

      form: {
        con_id: null,
        con_title: "",
        con_parent: null,
        con_parent_type: "",
        con_cat: null,
        con_type: "",
        con_sub_title: "",
        con_meta_title: "",
        con_keyword: "",
        con_desc: ""
      },

      contentTypes: [
        "page", "section", "banner", "feature", "course", "creator", "general"
      ],
      parentTypes: []
    };
  },

  computed: {
    websiteAreaOptions() {
      return [...new Set([
        "home",
        "event",
        "course",
        "creator",
        "blog",
        ...this.parentTypes
      ])];
    },

    isViewMode() {
      return this.modalMode === "view";
    },

    modalTitle() {
      if (this.modalMode === "create") {
        return "Add Content";
      }

      if (this.modalMode === "edit") {
        return "Edit Content";
      }

      return "Content Details";
    }
  },

  mounted() {
    this.loadStats();
    this.loadContents();
    this.loadCategories();
    this.loadCourses();
    this.loadCreators();
    this.loadBlogs();
  },

  methods: {
    // =====================================================
    // STATS
    // =====================================================

    async loadStats() {
      try {
        const res = await getContentMainStatsApi();

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.totalContents =
            data.totalContents ??
            data.total_content ??
            data.total ??
            0;

          this.totalCategories =
            data.totalCategories ??
            data.categories ??
            0;

          this.totalTypes =
            data.totalTypes ??
            data.contentTypes ??
            data.types ??
            0;

          this.thisMonth =
            data.thisMonth ??
            data.this_month ??
            data.newThisMonth ??
            0;
        }
      } catch (error) {
        console.error("Content main stats error:", error);
      }
    },

    // =====================================================
    // CONTENT LIST
    // =====================================================

    async loadContents() {
      this.loading = true;

      try {
        const res = await getContentMainsApi({
          page: this.page,
          limit: this.limit,
          search: this.search,
          type: this.typeFilter,
          parentType: this.parentTypeFilter,
          category: this.categoryFilter
        });

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.contents =
            data.contents ||
            data.contentMains ||
            data.rows ||
            [];

          this.totalContents =
            data.total ??
            data.totalContents ??
            this.contents.length;

          this.totalPages =
            data.totalPages ||
            Math.ceil(
              this.totalContents / this.limit
            ) ||
            1;

          this.contentTypes = data.types?.length
            ? data.types
            : this.contentTypes;
          this.parentTypes = data.parentTypes || this.parentTypes;
        } else {
          this.contents = [];
          this.totalContents = 0;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error(
          "Content main list error:",
          error
        );

        this.contents = [];
      } finally {
        this.loading = false;
      }
    },

    // =====================================================
    // CATEGORIES
    // IMPORTANT: cat_type = content
    // =====================================================

    async loadCategories() {
      try {
        const res = await getCategoriesApi({
          page: 1,
          limit: 100,
          search: "",
          cat_type: "content"
        });

        if (res && res.status !== false) {
          const data = res.data || res;

          this.categories = Array.isArray(data)
            ? data
            : data.categories || [];
        } else {
          this.categories = [];
        }
      } catch (error) {
        console.error(
          "Load Content Categories Error:",
          error
        );

        this.categories = [];
      }
    },

    // =====================================================
    // COURSES
    // =====================================================

    async loadCourses() {
      try {
        const res = await getCoursesApi({
          page: 1,
          limit: 100,
          search: ""
        });

        if (res && res.status !== false) {
          const data = res.data || res;

          this.courses = Array.isArray(data)
            ? data
            : data.courses ||
              data.rows ||
              [];
        } else {
          this.courses = [];
        }
      } catch (error) {
        console.error(
          "Load Content Courses Error:",
          error
        );

        this.courses = [];
      }
    },

    // =====================================================
    // CREATORS
    // =====================================================

    async loadCreators() {
      try {
        const res = await getCreatorsApi({
          page: 1,
          limit: 100,
          search: ""
        });

        if (res && res.status !== false) {
          const data = res.data || res;

          this.creators = Array.isArray(data)
            ? data
            : data.creators ||
              data.rows ||
              [];
        } else {
          this.creators = [];
        }
      } catch (error) {
        console.error(
          "Load Content Creators Error:",
          error
        );

        this.creators = [];
      }
    },

    // =====================================================
    // BLOGS
    // =====================================================

    async loadBlogs() {
      try {
        const res = await getBlogsApi({
          page: 1,
          limit: 100,
          search: ""
        });

        if (res && res.status !== false) {
          const data = res.data || res;

          this.blogs = Array.isArray(data)
            ? data
            : data.blogs ||
              data.rows ||
              [];
        } else {
          this.blogs = [];
        }
      } catch (error) {
        console.error(
          "Load Content Blogs Error:",
          error
        );

        this.blogs = [];
      }
    },

    // =====================================================
    // PARENT TYPE CHANGE
    // =====================================================

    changeParentType() {
      this.form.con_parent = null;
    },

    // =====================================================
    // FILTERS
    // =====================================================

    applyFilters() {
      this.page = 1;
      this.loadContents();
    },

    resetFilters() {
      this.search = "";
      this.typeFilter = "";
      this.parentTypeFilter = "";
      this.categoryFilter = "";
      this.page = 1;

      this.loadContents();
    },

    changePage(page) {
      if (
        page < 1 ||
        page > this.totalPages
      ) {
        return;
      }

      this.page = page;
      this.loadContents();
    },

    // =====================================================
    // CREATE
    // =====================================================

    openCreate() {
      this.resetForm();

      this.modalMode = "create";
      this.showModal = true;
    },

    // =====================================================
    // VIEW
    // =====================================================

    async openView(content) {
      await this.loadContentDetails(
        content.con_id,
        "view"
      );
    },

    // =====================================================
    // EDIT
    // =====================================================

    async openEdit(content) {
      await this.loadContentDetails(
        content.con_id,
        "edit"
      );
    },

    // =====================================================
    // LOAD CONTENT DETAILS
    // =====================================================

    async loadContentDetails(
      contentId,
      mode
    ) {
      this.loading = true;

      try {
        const res =
          await getContentMainApi(
            contentId
          );

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.form = {
            con_id:
              data.con_id ??
              contentId,

            con_title:
              data.con_title ?? "",

            con_parent:
              data.con_parent !== null &&
              data.con_parent !== undefined &&
              data.con_parent !== ""
                ? Number(data.con_parent)
                : null,

            con_parent_type:
              data.con_parent_type ?? "",

            con_cat:
              data.con_cat !== null &&
              data.con_cat !== undefined &&
              data.con_cat !== ""
                ? Number(data.con_cat)
                : null,

            con_type:
              data.con_type ?? "",

            con_sub_title:
              data.con_sub_title ?? "",

            con_meta_title:
              data.con_meta_title ?? "",

            con_keyword:
              data.con_keyword ?? "",

            con_desc:
              data.con_desc ?? ""
          };

          this.modalMode = mode;
          this.showModal = true;
        }
      } catch (error) {
        console.error(
          "Content main view error:",
          error
        );
      } finally {
        this.loading = false;
      }
    },

    // =====================================================
    // SAVE
    // =====================================================

    async saveContent() {
  const title = (this.form.con_title || "").trim();

  if (!title) {
    alert("Please enter Content Title.");
    return;
  }

  if (!this.form.con_type) {
    alert("Please select Content Type.");
    return;
  }

  this.saving = true;

  try {
    const payload = {
      ...this.form,

      con_title: title,

      con_parent:
        this.form.con_parent_type === "home"
          ? null
          : this.form.con_parent !== null &&
            this.form.con_parent !== ""
            ? Number(this.form.con_parent)
            : null,

      con_cat:
        this.form.con_cat !== null &&
        this.form.con_cat !== ""
          ? Number(this.form.con_cat)
          : null,
    };

    console.log("Content Main Save Payload:", payload);

    let res;

    if (this.modalMode === "edit") {
      res = await updateContentMainApi(payload);
    } else {
      res = await createContentMainApi(payload);
    }

    console.log("Content Main Save Response:", res);

    if (res?.status === true || res?.success === true) {
      alert(
        this.modalMode === "edit"
          ? "Content updated successfully."
          : "Content created successfully."
      );

      this.closeModal();

      await this.loadStats();
      await this.loadContents();

      return;
    }

    alert(
      res?.success ||
      res?.message ||
      "Content could not be saved. Please check the API response."
    );
  } catch (error) {
    console.error("Content main save error:", error);

    alert(
      error?.response?.data?.success ||
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong while saving content."
    );
  } finally {
    this.saving = false;
  }
},

    // =====================================================
    // DELETE
    // =====================================================

    async deleteContent(content) {
      if (!content?.con_id) {
        return;
      }

      if (
        !confirm(
          "Are you sure you want to delete this content?"
        )
      ) {
        return;
      }

      try {
        const res =
          await deleteContentMainApi(
            content.con_id
          );

        if (res?.status || res?.success) {
          await this.loadStats();

          if (
            this.contents.length === 1 &&
            this.page > 1
          ) {
            this.page--;
          }

          await this.loadContents();
        }
      } catch (error) {
        console.error(
          "Content main delete error:",
          error
        );
      }
    },

    // =====================================================
    // RESET FORM
    // =====================================================

    resetForm() {
      this.form = {
        con_id: null,
        con_title: "",
        con_parent: null,
        con_parent_type: "",
        con_cat: null,
        con_type: "",
        con_sub_title: "",
        con_meta_title: "",
        con_keyword: "",
        con_desc: ""
      };
    },

    // =====================================================
    // CLOSE
    // =====================================================

    closeModal() {
      this.showModal = false;
      this.modalMode = "view";
      this.resetForm();
    },

    // =====================================================
    // EDIT FROM VIEW
    // =====================================================

    editFromView() {
      this.modalMode = "edit";
    },

    // =====================================================
    // DATE
    // =====================================================

    formatDate(date) {
      if (!date) {
        return "—";
      }

      try {
        return new Date(date).toLocaleDateString(
          "en-IN",
          {
            day: "2-digit",
            month: "short",
            year: "numeric"
          }
        );
      } catch (error) {
        return date;
      }
    },

    formatLabel(value) {
      return String(value || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },

    formatAreaLabel(value) {
      const labels = {
        home: "Home page",
        event: "Events",
        course: "Course page",
        creator: "Creator page",
        blog: "Blog page"
      };

      return labels[value] || this.formatLabel(value) || "Unassigned";
    }
  }
};
</script>

<style scoped>
.content-main-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s ease;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.secondary-btn:hover {
  background: #f8fafc;
}

.reset-btn {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-card span {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-card strong {
  display: block;
  font-size: 22px;
  line-height: 1;
}

/* Main Card */
.content-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 360px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  outline: none;
  font-size: 14px;
  color: #334155;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-select {
  padding: 0 34px 0 12px;
  min-width: 145px;
}

.search-box input:focus,
.filter-select:focus,
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  border-color: #2563eb;
}

/* Table */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1100px;
}

thead {
  background: #f8fafc;
}

th {
  padding: 13px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  vertical-align: middle;
}

tbody tr:hover {
  background: #fafcff;
}

.action-column {
  width: 125px;
  text-align: right;
}

.content-info {
  display: flex;
  align-items: center;
  gap: 11px;
}

.content-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.content-info strong {
  display: block;
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e293b;
  font-size: 13px;
}

.content-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.content-info .content-subtitle {
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
}

.parent-info span {
  display: block;
  font-weight: 600;
  color: #475569;
}

.parent-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.category-badge,
.type-badge,
.area-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 5px 9px;
  font-size: 11px;
  font-weight: 600;
}

.category-badge {
  background: #f1f5f9;
  color: #475569;
}

.type-badge {
  background: #eff6ff;
  color: #2563eb;
}

.area-badge {
  background: #ecfdf5;
  color: #047857;
}

.subtitle-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
}

.muted {
  color: #94a3b8;
}

.date-text {
  white-space: nowrap;
  color: #64748b;
}

/* Actions */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.icon-btn.view {
  color: #2563eb;
}

.icon-btn.edit {
  color: #64748b;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h3 {
  margin: 12px 0 5px;
  font-size: 16px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.pagination-info {
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 7px;
  color: #475569;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.pagination-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}

.view-modal {
  max-width: 720px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.modal-header h2 {
  margin: 0 0 5px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  background: #f8fafc;
  border-radius: 7px;
  color: #64748b;
  cursor: pointer;
  font-size: 20px;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 13px;
  color: #334155;
  outline: none;
  background: #fff;
}

.form-group input,
.form-group select {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

/* Details */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item.full {
  grid-column: 1 / -1;
}

.detail-item label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.detail-item strong {
  color: #1e293b;
  font-size: 15px;
}

.detail-item span,
.detail-item p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.description-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 13px;
  color: #475569;
  line-height: 1.6;
  font-size: 13px;
}

/* Delete */
.delete-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}

.delete-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* Responsive */
@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .content-main-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    max-width: none;
    width: 100%;
  }

  .filter-select {
    flex: 1;
  }

  .form-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full,
  .detail-item.full {
    grid-column: auto;
  }

  .pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>